import axios from "axios";

import { redact } from "@/src/lib/logging/redact";
import { getRequestContext } from "@/src/lib/request-context";
import { isServer } from "@/src/lib/runtime";

type Meta = { [key: string]: any };

// ANSI color codes for terminal
const COLORS = {
  RESET: "\x1b[0m",
  RED: "\x1b[31m",
  YELLOW: "\x1b[33m",
  BRIGHT_BLUE: "\x1b[38;5;33m",
  BRIGHT_PINK: "\x1b[38;5;213m",
  GRAY: "\x1b[38;5;240m",
};

// Log prefixes for cloud viewers
const MARKERS = {
  WARNING: "⚠️ ",
  ERROR: "🔴 ",
  INFO: "",
  DEBUG: "🔍 ",
};

/**
 * Get the current file name using multiple detection methods
 */
function getFileName(): string | null {
  if (!isServer) {
    return null;
  }

  // Try Node.js __filename first
  if (typeof __filename !== "undefined") {
    return __filename.split("/").pop()?.split("\\").pop() || "unknown";
  }

  // Try import.meta.url
  try {
    const url = import.meta.url;

    return url.split("/").pop() || "unknown";
  } catch {
    return "unknown";
  }
}

// Logger wrapper files whose frames should be skipped when walking the stack.
const LOGGER_WRAPPER_PATHS = ["logging/logger", "logging/comms-logger", "ehr/athena/athena-logger"];

// Function names that appear in VH source frames but don't identify the actual caller.
const OPAQUE_CALLER_NAMES = new Set(["logAndThrow", "Timeout._onTimeout", "Immediate._onImmediate", "<anonymous>"]);

// Next.js sets Error.prepareStackTrace at startup (patch-error-inspect.js), which
// blocks Node.js's source-map-aware stack formatting. Temporarily removing it lets
// the default formatter run, producing stacks with original TS paths and function names.
function captureSourceMappedStack(omitAbove: Function): string | null {
  const savedPrepare = Error.prepareStackTrace;

  Reflect.deleteProperty(Error, "prepareStackTrace");

  try {
    const obj: { stack?: string } = {};

    Error.captureStackTrace(obj, omitAbove);

    return obj.stack ?? null;
  } finally {
    Error.prepareStackTrace = savedPrepare;
  }
}

// Extracts the function name from a V8 stack frame string.
// Returns null for frames without a named function (bare file paths) or unparseable frames.
function parseFunctionName(frame: string): string | null {
  const match = frame.match(/at (?:async\s+)?(?:new\s+)?(\S+)/);

  if (!match) return null;

  const raw = match[1];

  if (raw.includes("/") || raw.includes("\\")) return null;

  // V8 prefixes static methods with "Function." and object-property functions with "Object."
  if (raw.startsWith("Function.")) return raw.slice("Function.".length);
  if (raw.startsWith("Object.")) return raw.slice("Object.".length);

  return raw;
}

// Walks the call stack to find the first frame that belongs to application
// source code and has a meaningful function name. Returns null if no such
// frame exists (e.g., called from an anonymous callback or timer).
function getCallerFunctionName(): string | null {
  if (!isServer) return null;
  if (typeof Error.captureStackTrace !== "function") return null;

  try {
    const stack = captureSourceMappedStack(getCallerFunctionName);

    if (!stack) return null;

    for (const frame of stack.split("\n")) {
      if (!frame.includes(".ts:") && !frame.includes(".tsx:")) continue;
      if (LOGGER_WRAPPER_PATHS.some((p) => frame.includes(p))) continue;

      const name = parseFunctionName(frame);

      if (!name) continue;
      if (OPAQUE_CALLER_NAMES.has(name)) continue;

      return name;
    }
  } catch {
    // Auto-detection is best-effort; failures silently degrade to no functionName.
  }

  return null;
}

function logAsync(
  severity: "INFO" | "WARNING" | "ERROR" | "DEBUG",
  message: any,
  meta: Meta,
  prettyPrint: boolean = false
) {
  // Must capture synchronously — the async boundary below erases the original call stack.
  const autoFunctionName = meta.functionName ? null : getCallerFunctionName();

  Promise.resolve().then(async () => {
    try {
      if (severity === "DEBUG" && process.env.LOG_LEVEL?.toLowerCase() !== "debug") {
        return;
      }

      let logObj, logArgs;
      const isLocalTerminal = process.env.NODE_ENV !== "production";
      const timestamp = new Date().toISOString();

      const requestContextMeta: Meta = await getRequestContext();

      let processedMessage = message;
      const autoFn = autoFunctionName ? { functionName: autoFunctionName } : {};
      let processedMeta: Meta = { ...autoFn, ...requestContextMeta, ...meta };

      if (message instanceof Error) {
        const errorDetails = extractErrorDetails(message);

        processedMessage = errorDetails.error;

        processedMeta = { ...autoFn, ...requestContextMeta, ...meta, error: errorDetails };
      }

      if (meta.error) {
        const errorDetails = extractErrorDetails(meta.error);

        processedMeta = { ...autoFn, ...requestContextMeta, ...meta, error: errorDetails };
      }

      const markedMessage = `${MARKERS[severity]}${processedMessage}`;

      logObj =
        process.env.NODE_ENV === "development"
          ? { severity, message: markedMessage, ...processedMeta, timestamp }
          : redact({ severity, message: markedMessage, ...processedMeta, timestamp });

      if (!isServer) {
        logObj = { severity, ...meta };

        switch (severity) {
          case "WARNING":
            logArgs = [`%c${MARKERS.WARNING}${message}`, "color: orange; font-weight: bold;", ...Object.values(logObj)];
            break;
          case "ERROR":
            logArgs = [`%c${MARKERS.ERROR}${message}`, "color: red; font-weight: bold;", ...Object.values(logObj)];
            break;
          case "INFO":
            logArgs = [`%c${MARKERS.INFO}${message}`, ...Object.values(logObj)];
            break;
          case "DEBUG":
            logArgs = [`%c${MARKERS.DEBUG}${message}`, "color: gray;", ...Object.values(logObj)];
            break;
        }
      } else if (isLocalTerminal) {
        const logObjTyped = logObj as { message: string; [key: string]: unknown };
        const { message: logMessage, ...rest } = logObjTyped;

        switch (severity) {
          case "WARNING":
            logArgs = [
              `${COLORS.YELLOW}${logMessage}${COLORS.RESET} ${prettyPrint ? JSON.stringify(rest, null, 2) : JSON.stringify(rest)} \n`,
            ];
            break;
          case "ERROR":
            logArgs = [
              `${COLORS.RED}${logMessage}${COLORS.RESET} ${prettyPrint ? JSON.stringify(rest, null, 2) : JSON.stringify(rest)} \n`,
            ];
            break;
          case "INFO":
            logArgs = [
              `${COLORS.BRIGHT_BLUE}${logMessage}${COLORS.RESET} ${prettyPrint ? JSON.stringify(rest, null, 2) : JSON.stringify(rest)} \n`,
            ];
            break;
          case "DEBUG":
            logArgs = [
              `${COLORS.BRIGHT_PINK}${logMessage}${COLORS.RESET} ${prettyPrint ? JSON.stringify(rest, null, 2) : JSON.stringify(rest)} \n`,
            ];
            break;
          default:
            logArgs = [
              `${COLORS.BRIGHT_BLUE}${logMessage}${COLORS.RESET} ${prettyPrint ? JSON.stringify(rest, null, 2) : JSON.stringify(rest)} \n`,
            ];
            break;
        }
      } else {
        // For cloud logs, just stringify the logObj
        logArgs = [JSON.stringify(logObj)];
      }

      switch (severity) {
        case "INFO":
          console.info(...logArgs);
          break;
        case "WARNING":
          console.warn(...logArgs);
          break;
        case "ERROR":
          console.error(...logArgs);
          break;
        case "DEBUG":
          console.debug(...logArgs);
          break;
      }
    } catch (err) {
      console.error(`[LOGGER ERROR] Failed to log ${severity}:`, err);
    }
  });
}

export const logger = {
  info: (message: any, meta?: Meta, prettyPrint: boolean = false) => {
    const fileName = getFileName();

    return logAsync("INFO", message, { ...(fileName && { fileName }), ...meta }, prettyPrint);
  },
  warn: (message: any, meta?: Meta) => {
    const fileName = getFileName();

    return logAsync("WARNING", message, { ...(fileName && { fileName }), ...meta });
  },
  error: (message: any, meta?: Meta, prettyPrint: boolean = false) => {
    const fileName = getFileName();

    return logAsync("ERROR", message, { ...(fileName && { fileName }), ...meta }, prettyPrint);
  },
  debug: (message: any, meta?: Meta, prettyPrint: boolean = false) => {
    const fileName = getFileName();

    return logAsync("DEBUG", message, { ...(fileName && { fileName }), ...meta }, prettyPrint);
  },
};

export type Logger = typeof logger & {
  logAndThrow: (message: string, meta?: Meta, logAsError?: boolean) => never;
};

export const createLogger = (sharedMeta?: Object): Logger => {
  const logMethods = Object.entries(logger).reduce(
    (acc, [method, fn]) => {
      acc[method as keyof typeof logger] = (message: any, meta?: Meta, prettyPrint: boolean = false) => {
        return fn(message, { ...meta, ...sharedMeta }, prettyPrint);
      };

      return acc;
    },
    {} as typeof logger
  );

  return {
    ...logMethods,
    logAndThrow: (message: string, meta?: Meta, logAsError: boolean = false): never => {
      if (logAsError) {
        logMethods.error(message, { ...meta, ...sharedMeta, success: false });
      } else {
        logMethods.warn(message, { ...meta, ...sharedMeta, success: false });
      }

      throw new Error(message, { cause: meta?.error });
    },
  };
};

// todo: move to error.ts
function extractCauseChain(cause: unknown, depth = 0): string | undefined {
  if (depth > 4 || !cause) return undefined;

  if (cause instanceof Error) {
    const nested = extractCauseChain(cause.cause, depth + 1);

    return nested ? `${cause.message} → ${nested}` : cause.message;
  }

  if (typeof cause === "object") {
    try {
      return JSON.stringify(cause);
    } catch {
      return String(cause);
    }
  }

  return String(cause);
}

export function extractErrorDetails(error: unknown) {
  if (axios.isAxiosError(error)) {
    return {
      error: error.message,
      name: error.name,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestMethod: error.config?.method,
      requestUrl: error.config?.url,
      requestBaseURL: error.config?.baseURL,
      cause: extractCauseChain(error.cause),
    };
  }

  if (error instanceof Error) {
    return {
      error: error.message,
      name: error.name,
      cause: extractCauseChain(error.cause),
    };
  }

  return {
    error: JSON.stringify(error, null, 2),
  };
}

export function logAndThrow(message: string, meta?: Meta, logAsError: boolean = false): never {
  if (logAsError) {
    logger.error(message, { ...meta, error: extractErrorDetails(meta?.error) });
  } else {
    logger.warn(message, { ...meta, error: extractErrorDetails(meta?.error) });
  }

  throw new Error(message, { cause: meta?.error });
}
